import { useQuizTimer } from "@/client/ui/hooks/useQuizTimer";
import { useUserSettingToggle } from "@/client/ui/hooks/useUserSettingToggle";
import type {
  MistakeType,
  OneCorrectPairQuestion,
  OneCorrectPairQuestionChoice,
  UnsavedSkillRating,
} from "@/data/model";
import { QuestionFlagKind } from "@/data/model";
import type { OneCorrectPairQuestionRating } from "@/data/questions/oneCorrectPair";
import {
  gradeOneCorrectPairQuestion,
  oneCorrectPairChoiceText,
} from "@/data/questions/oneCorrectPair";
import {
  autoCheckUserToggleSetting,
  newSkillLessonUserToggleSetting,
} from "@/data/userSettings";
import { longestTextByCharacters } from "@/util/unicode";
import { invariant } from "@pinyinly/lib/invariant";
import { useState } from "react";
import { Text } from "@/client/ui/Text";
import { View } from "@/client/ui/View";
import { NewSkillModal } from "./NewSkillModal";
import { QuizFlagText } from "./QuizFlagText";
import { QuizDeckQuestionSkeleton } from "./QuizDeckQuestionSkeleton";
import { TextAnswerButton } from "./TextAnswerButton";
import type {
  TextAnswerButtonFontSize,
  TextAnswerButtonState,
} from "./TextAnswerButton.utils";
import {
  ratingToButtonState,
  textAnswerButtonFontSize,
} from "./TextAnswerButton.utils";
import { WikiHanziModal } from "./WikiHanziModal";

const buttonThickness = 4;
const gap = 12;

export function QuizDeckOneCorrectPairQuestion({
  question,
  onNext,
  onRating,
  onChangeAnswer,
}: {
  question: OneCorrectPairQuestion;
  onNext: () => void;
  onRating: (
    ratings: readonly UnsavedSkillRating[],
    mistakes: readonly MistakeType[],
  ) => void;
  onChangeAnswer: () => void;
}) {
  const { prompt, answer, groupA, groupB, flag } = question;

  const { isEnabled: autoCheck } = useUserSettingToggle(
    autoCheckUserToggleSetting,
  );
  const { isEnabled: showNewSkillLesson } = useUserSettingToggle(
    newSkillLessonUserToggleSetting,
  );

  const [selectedAChoice, setSelectedAChoice] =
    useState<OneCorrectPairQuestionChoice>();
  const [selectedBChoice, setSelectedBChoice] =
    useState<OneCorrectPairQuestionChoice>();
  const [rating, setRating] = useState<OneCorrectPairQuestionRating>();

  // Setup the timer to measure how fast they answer the question.
  const timer = useQuizTimer();

  const choiceRowCount = Math.max(groupA.length, groupB.length);

  if (__DEV__) {
    invariant(answer.as.every((a) => groupA.includes(a)));
    invariant(answer.bs.every((b) => groupB.includes(b)));
  }

  const submitChoices = (
    aChoice: OneCorrectPairQuestionChoice,
    bChoice: OneCorrectPairQuestionChoice,
  ) => {
    const durationMs = timer.endTime ?? timer.getDurationMs();
    const questionRating = gradeOneCorrectPairQuestion(
      answer,
      aChoice,
      bChoice,
      durationMs,
    );

    setRating(questionRating);
    onRating(
      questionRating.skillRatings,
      questionRating.correct ? [] : questionRating.mistakes,
    );
  };

  const groupAFontSize = textAnswerButtonFontSize(
    longestTextByCharacters(
      groupA.map((choice) => oneCorrectPairChoiceText(choice)),
    ),
  );
  const groupBFontSize = textAnswerButtonFontSize(
    longestTextByCharacters(
      groupB.map((choice) => oneCorrectPairChoiceText(choice)),
    ),
  );

  return (
    <QuizDeckQuestionSkeleton
      rating={rating}
      isUserAnswerProvided={
        selectedAChoice !== undefined && selectedBChoice !== undefined
      }
      onSubmit={() => {
        if (selectedAChoice == null || selectedBChoice == null) {
          return;
        } else if (rating == null) {
          submitChoices(selectedAChoice, selectedBChoice);
        } else {
          onNext();
        }
      }}
      onChangeAnswer={onChangeAnswer}
      skill={answer.skill}
    >
      {flag?.kind === QuestionFlagKind.NewSkill && showNewSkillLesson ? (
        <NewSkillModal passivePresentation skill={question.answer.skill} />
      ) : null}

      {flag == null ? null : <QuizFlagText flag={flag} />}
      <View>
        <Text className="font-sans text-xl font-bold text-fg-loud">
          {prompt}
        </Text>
      </View>
      <View className="flex-1 justify-center py-4">
        <View
          className="flex-1 flex-row gap-7"
          style={{
            maxHeight:
              choiceRowCount * 80 +
              (choiceRowCount - 1) * gap +
              buttonThickness,
          }}
        >
          {/* Group A */}
          <View className="flex-1" style={{ gap: gap + buttonThickness }}>
            {groupA.map((a, i) => (
              <ChoiceButton
                key={i}
                choice={a}
                fontSize={groupAFontSize}
                answered={rating != null}
                state={
                  selectedAChoice === undefined
                    ? `default`
                    : a === selectedAChoice
                      ? rating == null
                        ? `selected`
                        : ratingToButtonState(rating.rating)
                      : selectedBChoice === undefined
                        ? `default`
                        : `dimmed`
                }
                onPress={() => {
                  if (rating == null) {
                    const newSelectedAChoice =
                      selectedAChoice === a ? undefined : a;
                    setSelectedAChoice(newSelectedAChoice);
                    timer.recordChoice(
                      newSelectedAChoice != null &&
                        answer.as.includes(newSelectedAChoice),
                    );

                    // Support auto-submit
                    if (
                      autoCheck &&
                      newSelectedAChoice != null &&
                      selectedBChoice != null
                    ) {
                      submitChoices(newSelectedAChoice, selectedBChoice);
                    }
                  } else if (rating.correct) {
                    // If the answer is correct, this is a shortcut to the next
                    // question to avoid moving the mouse.
                    onNext();
                  }
                }}
              />
            ))}
          </View>
          {/* Group B */}
          <View className="flex-1" style={{ gap: gap + buttonThickness }}>
            {groupB.map((b, i) => (
              <ChoiceButton
                key={i}
                choice={b}
                fontSize={groupBFontSize}
                answered={rating != null}
                state={
                  selectedBChoice === undefined
                    ? `default`
                    : b === selectedBChoice
                      ? rating == null
                        ? `selected`
                        : ratingToButtonState(rating.rating)
                      : selectedAChoice === undefined
                        ? `default`
                        : `dimmed`
                }
                onPress={() => {
                  if (rating == null) {
                    const newSelectedBChoice =
                      selectedBChoice === b ? undefined : b;
                    setSelectedBChoice(newSelectedBChoice);
                    timer.recordChoice(
                      newSelectedBChoice != null &&
                        answer.bs.includes(newSelectedBChoice),
                    );

                    // Support auto-submit
                    if (
                      autoCheck &&
                      selectedAChoice != null &&
                      newSelectedBChoice != null
                    ) {
                      submitChoices(selectedAChoice, newSelectedBChoice);
                    }
                  } else if (rating.correct) {
                    // If the answer is correct, this is a shortcut to the next
                    // question to avoid moving the mouse.
                    onNext();
                  }
                }}
              />
            ))}
          </View>
        </View>
      </View>
    </QuizDeckQuestionSkeleton>
  );
}

const ChoiceButton = ({
  state,
  choice,
  fontSize,
  answered,
  onPress,
}: {
  state: TextAnswerButtonState;
  choice: OneCorrectPairQuestionChoice;
  fontSize: TextAnswerButtonFontSize;
  answered: boolean;
  onPress: (choice: OneCorrectPairQuestionChoice) => void;
}) => {
  const shouldShowWikiModal = answered && choice.kind === `hanzi`;

  return (
    <TextAnswerButton
      onPress={() => {
        onPress(choice);
      }}
      fontSize={fontSize}
      state={state}
      className="flex-1"
      text={oneCorrectPairChoiceText(choice)}
      renderWikiModal={
        shouldShowWikiModal
          ? (onDismiss) => (
              <WikiHanziModal hanzi={choice.value} onDismiss={onDismiss} />
            )
          : undefined
      }
    />
  );
};
